import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Send, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const EVENT_TYPES = ["Wedding", "Concert", "Festival", "City Event", "Corporate Event", "Private Party", "Film Production", "Sporting Event", "Other"];

const ADDONS = [
  { key: "generator", label: "Generator" },
  { key: "water_supply", label: "Water Supply" },
  { key: "attendant", label: "Restroom Attendant" },
  { key: "extra_servicing", label: "Additional Servicing" },
  { key: "multi_day_servicing", label: "Multi-Day Servicing" },
  { key: "extended_rental", label: "Extended Rental Time" },
  { key: "custom_signage", label: "Custom Signage" },
  { key: "emergency_delivery", label: "Emergency Delivery" },
  { key: "special_placement", label: "Special Placement Assistance" },
];

const STEPS = ["Event", "Location", "Trailers", "Add-ons", "Contact", "Summary"];

export default function Booking() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [availability, setAvailability] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    event_type: "Wedding",
    event_date: "",
    event_end_date: "",
    start_time: "",
    end_time: "",
    is_multi_day: false,
    guest_count: "",
    venue_name: "",
    event_address: "",
    event_city: "",
    event_zip: "",
    outdoor_or_indoor: "outdoor",
    delivery_notes: "",
    placement_notes: "",
    surface_type: "grass",
    power_available: true,
    water_available: true,
    trailers_requested: Number(sp.get("trailers")) || 1,
    addons: [],
    customer_name: "",
    company: "",
    customer_email: "",
    customer_phone: "",
    preferred_contact: "email",
    planner_name: "",
    venue_contact: "",
    additional_notes: "",
    agree_terms: false,
  });

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // fetch availability when date changes
  useEffect(() => {
    if (!form.event_date) return;
    (async () => {
      try {
        const { data } = await api.get(`/trailers/availability?event_date=${form.event_date}`);
        setAvailability(data);
      } catch (e) {
        console.error("availability check failed", e);
      }
    })();
  }, [form.event_date]);

  const canNext = useMemo(() => {
    if (step === 0) return !!(form.event_type && form.event_date && form.guest_count);
    if (step === 1) return !!(form.event_address && form.event_city);
    if (step === 2) return form.trailers_requested === 1 || form.trailers_requested === 2;
    if (step === 4) return !!(form.customer_name && form.customer_email && form.customer_phone);
    if (step === 5) return form.agree_terms;
    return true;
  }, [step, form]);

  const next = () => {
    if (!canNext) {
      toast.error("Please complete the required fields.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submitRequest = async () => {
    if (!form.agree_terms) { toast.error("Please agree to the rental terms."); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, guest_count: Number(form.guest_count) || 1 };
      const { data: booking } = await api.post("/bookings", payload);
      navigate(`/booking/received?booking_id=${booking.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="section">
        <div className="mx-auto max-w-3xl">
          <div className="overline">Request a Booking</div>
          <h1 className="h-display mt-3 text-4xl md:text-5xl leading-[1.05]">Let's plan your event.</h1>
          <p className="mt-4 text-graphite">Takes about five minutes. We'll review your request and reply within 24 hours with availability and a final quote.</p>

          <div className="mt-10">
            <div className="flex justify-between text-xs text-graphite mb-3">
              <span>Step {step + 1} of {STEPS.length}</span>
              <span>{STEPS[step]}</span>
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
          </div>

          <div className="mt-10 card-soft p-8 md:p-10">
            {step === 0 && <StepEvent form={form} upd={upd} />}
            {step === 1 && <StepLocation form={form} upd={upd} />}
            {step === 2 && <StepTrailers form={form} upd={upd} availability={availability} />}
            {step === 3 && <StepAddons form={form} upd={upd} />}
            {step === 4 && <StepContact form={form} upd={upd} />}
            {step === 5 && <StepSummary form={form} upd={upd} />}

            <div className="mt-10 flex flex-col-reverse md:flex-row justify-between gap-3">
              <button type="button" onClick={back} disabled={step === 0} data-testid={TID.bookingBack} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={next} data-testid={TID.bookingNext} className="btn-primary">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button type="button" onClick={submitRequest} disabled={submitting || !form.agree_terms} data-testid={TID.bookingPay} className="btn-primary disabled:opacity-50">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send booking request</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ------- Step components -------
function Label({ children }) { return <span className="overline block mb-2">{children}</span>; }
function Field({ label, children }) { return <label className="block">{label && <Label>{label}</Label>}{children}</label>; }
const inputCls = "w-full h-12 rounded-2xl border border-stone bg-ivory px-4 text-charcoal placeholder:text-mist focus:outline-none focus:ring-2 focus:ring-pebble focus:border-pebble transition-shadow";

function StepEvent({ form, upd }) {
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Event type">
          <select data-testid="booking-event-type" className={inputCls} value={form.event_type} onChange={(e) => upd("event_type")(e.target.value)}>
            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Approx. guest count">
          <input data-testid="booking-guest-count" type="number" min="1" className={inputCls} value={form.guest_count} onChange={(e) => upd("guest_count")(e.target.value)} />
        </Field>
        <Field label="Event date">
          <input data-testid="booking-event-date" type="date" className={inputCls} value={form.event_date} onChange={(e) => upd("event_date")(e.target.value)} />
        </Field>
        <Field label="End date (if multi-day)">
          <input data-testid="booking-event-end-date" type="date" className={inputCls} value={form.event_end_date} onChange={(e) => { upd("event_end_date")(e.target.value); if (e.target.value) upd("is_multi_day")(true); }} />
        </Field>
        <Field label="Start time">
          <input data-testid="booking-start-time" type="time" className={inputCls} value={form.start_time} onChange={(e) => upd("start_time")(e.target.value)} />
        </Field>
        <Field label="End time">
          <input data-testid="booking-end-time" type="time" className={inputCls} value={form.end_time} onChange={(e) => upd("end_time")(e.target.value)} />
        </Field>
      </div>
      <label className="flex items-center gap-3 text-sm mt-2">
        <input type="checkbox" checked={form.is_multi_day} onChange={(e) => upd("is_multi_day")(e.target.checked)} data-testid="booking-multi-day" />
        This is a multi-day event
      </label>
    </div>
  );
}

function StepLocation({ form, upd }) {
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Venue name">
          <input data-testid="booking-venue-name" className={inputCls} value={form.venue_name} onChange={(e) => upd("venue_name")(e.target.value)} placeholder="e.g. Red Rock Chapel" />
        </Field>
        <Field label="Event city">
          <input data-testid="booking-event-city" className={inputCls} value={form.event_city} onChange={(e) => upd("event_city")(e.target.value)} placeholder="St. George" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Event address">
            <input data-testid="booking-event-address" className={inputCls} value={form.event_address} onChange={(e) => upd("event_address")(e.target.value)} placeholder="Street address" />
          </Field>
        </div>
        <Field label="ZIP code">
          <input data-testid="booking-event-zip" className={inputCls} value={form.event_zip} onChange={(e) => upd("event_zip")(e.target.value)} />
        </Field>
        <Field label="Setting">
          <select data-testid="booking-outdoor-indoor" className={inputCls} value={form.outdoor_or_indoor} onChange={(e) => upd("outdoor_or_indoor")(e.target.value)}>
            <option value="outdoor">Outdoor</option>
            <option value="indoor">Indoor</option>
            <option value="mixed">Both</option>
          </select>
        </Field>
        <Field label="Surface type">
          <select data-testid="booking-surface-type" className={inputCls} value={form.surface_type} onChange={(e) => upd("surface_type")(e.target.value)}>
            <option>grass</option><option>gravel</option><option>concrete</option><option>asphalt</option><option>dirt</option>
          </select>
        </Field>
        <Field label="Power availability">
          <select data-testid="booking-power" className={inputCls} value={String(form.power_available)} onChange={(e) => upd("power_available")(e.target.value === "true")}>
            <option value="true">On-site power available</option>
            <option value="false">No power (generator needed)</option>
          </select>
        </Field>
        <Field label="Water availability">
          <select data-testid="booking-water" className={inputCls} value={String(form.water_available)} onChange={(e) => upd("water_available")(e.target.value === "true")}>
            <option value="true">On-site water available</option>
            <option value="false">No water (supply needed)</option>
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Delivery access notes">
            <textarea data-testid="booking-delivery-notes" rows={3} className={`${inputCls} h-auto py-3`} value={form.delivery_notes} onChange={(e) => upd("delivery_notes")(e.target.value)} placeholder="Gate codes, narrow entries, slope, etc." />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Trailer placement notes">
            <textarea data-testid="booking-placement-notes" rows={3} className={`${inputCls} h-auto py-3`} value={form.placement_notes} onChange={(e) => upd("placement_notes")(e.target.value)} placeholder="Preferred placement, distance from event area, etc." />
          </Field>
        </div>
      </div>
    </div>
  );
}

function StepTrailers({ form, upd, availability }) {
  const opts = [
    { value: 1, title: "One Trailer", desc: "3 private stalls · best for smaller events" },
    { value: 2, title: "Two Trailers", desc: "6 total stalls · larger events or split locations" },
    { value: 0, title: "I'm not sure", desc: "Our team will help you decide." },
  ];
  return (
    <div className="space-y-5">
      <div className="grid gap-4">
        {opts.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => upd("trailers_requested")(o.value)}
            data-testid={`booking-trailer-option-${o.value}`}
            className={`text-left p-6 rounded-2xl border transition-all ${form.trailers_requested === o.value ? "border-charcoal bg-champagne/40 ring-1 ring-charcoal" : "border-stone bg-white hover:border-pebble"}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="h-display text-xl">{o.title}</div>
                <div className="text-sm text-graphite mt-1">{o.desc}</div>
              </div>
              {form.trailers_requested === o.value && <Check className="h-5 w-5 text-charcoal" />}
            </div>
          </button>
        ))}
      </div>
      {availability && form.event_date && (
        <div className="p-5 rounded-2xl bg-sand border border-stone text-sm">
          <div className="overline">Availability for {form.event_date}</div>
          <div className="mt-2 space-y-1">
            <div>Trailer 1: {availability.trailer_1_available ? "Available" : "Booked"}</div>
            <div>Trailer 2: {availability.trailer_2_available ? "Available" : "Booked"}</div>
          </div>
          {!availability.both_available && form.trailers_requested === 2 && (
            <div className="mt-3 text-charcoal">Requesting both trailers on this date requires manual confirmation.</div>
          )}
        </div>
      )}
    </div>
  );
}

function StepAddons({ form, upd }) {
  const toggle = (k) => {
    const set = new Set(form.addons);
    set.has(k) ? set.delete(k) : set.add(k);
    upd("addons")(Array.from(set));
  };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {ADDONS.map((a) => {
        const on = form.addons.includes(a.key);
        return (
          <button
            key={a.key}
            type="button"
            onClick={() => toggle(a.key)}
            data-testid={`booking-addon-${a.key}`}
            className={`text-left p-5 rounded-2xl border transition-all ${on ? "border-charcoal bg-champagne/40 ring-1 ring-charcoal" : "border-stone bg-white hover:border-pebble"}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{a.label}</div>
              </div>
              {on && <Check className="h-4 w-4" />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function StepContact({ form, upd }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Field label="Full name"><input data-testid="booking-customer-name" className={inputCls} value={form.customer_name} onChange={(e) => upd("customer_name")(e.target.value)} /></Field>
      <Field label="Company / organization (optional)"><input data-testid="booking-company" className={inputCls} value={form.company} onChange={(e) => upd("company")(e.target.value)} /></Field>
      <Field label="Email"><input data-testid="booking-customer-email" type="email" className={inputCls} value={form.customer_email} onChange={(e) => upd("customer_email")(e.target.value)} /></Field>
      <Field label="Mobile phone"><input data-testid="booking-customer-phone" type="tel" className={inputCls} value={form.customer_phone} onChange={(e) => upd("customer_phone")(e.target.value)} /></Field>
      <Field label="Preferred contact">
        <select data-testid="booking-preferred-contact" className={inputCls} value={form.preferred_contact} onChange={(e) => upd("preferred_contact")(e.target.value)}>
          <option value="email">Email</option><option value="phone">Phone</option><option value="text">Text</option>
        </select>
      </Field>
      <Field label="Event planner name (optional)"><input data-testid="booking-planner-name" className={inputCls} value={form.planner_name} onChange={(e) => upd("planner_name")(e.target.value)} /></Field>
      <Field label="Venue contact (optional)"><input data-testid="booking-venue-contact" className={inputCls} value={form.venue_contact} onChange={(e) => upd("venue_contact")(e.target.value)} /></Field>
      <div className="md:col-span-2">
        <Field label="Additional notes">
          <textarea data-testid="booking-additional-notes" rows={4} className={`${inputCls} h-auto py-3`} value={form.additional_notes} onChange={(e) => upd("additional_notes")(e.target.value)} />
        </Field>
      </div>
    </div>
  );
}

function StepSummary({ form, upd }) {
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <SummaryRow k="Event" v={`${form.event_type} · ${form.event_date}${form.event_end_date ? " → " + form.event_end_date : ""}`} />
        <SummaryRow k="Guests" v={form.guest_count || "—"} />
        <SummaryRow k="Location" v={`${form.event_address || "—"}, ${form.event_city || "—"}`} />
        <SummaryRow k="Trailers" v={form.trailers_requested === 2 ? "Two (6 stalls)" : form.trailers_requested === 1 ? "One (3 stalls)" : "Unsure"} />
        <SummaryRow k="Add-ons" v={form.addons.length ? form.addons.join(", ") : "None"} />
        <SummaryRow k="Contact" v={`${form.customer_name} · ${form.customer_email}`} />
      </div>

      <div className="rounded-2xl bg-sand border border-stone p-5 text-xs text-graphite leading-relaxed">
        This is a <strong>booking request</strong>, not a final reservation. Our team will review your event details, confirm trailer availability, and reply with a custom quote and next steps — typically within 24 hours.
      </div>

      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" data-testid="booking-agree-terms" className="mt-1" checked={form.agree_terms} onChange={(e) => upd("agree_terms")(e.target.checked)} />
        <span>I agree to the rental terms, cancellation, refund, damage, delivery, weather, and power/water policies.</span>
      </label>
    </div>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div>
      <div className="overline">{k}</div>
      <div className="mt-1 text-charcoal">{v}</div>
    </div>
  );
}
